import React, { Component, createRef } from 'react';
import * as faceapi from 'face-api.js';
import './BiometricSelfie.css';
import axios from 'axios';
import { getApiHeaderGuest } from '../../../utils/CommonFunctions';
import PageLoader from '../../Loader/loader';

const SERVER_URL = process.env.REACT_APP_SERVER_URL;

const MODELS_URL = '/models';
const LIVENESS_HOLD_MS = 1500;
const DETECTION_INTERVAL_MS = 300;
const MIN_FACE_HEIGHT_RATIO = 0.25;
const MAX_CENTER_OFFSET = 0.28;
const EYE_AR_CLOSE = 0.25;
const EYE_AR_OPEN = 0.26;
const REQUIRED_BLINKS = 2;


const PASSIVE_SCAN_FRAMES = 10;
const EAR_STDDEV_STRONG = 0.008;
const EAR_STDDEV_WEAK = 0.005;
const LANDMARK_STDDEV_STRONG = 0.004;
const LANDMARK_STDDEV_WEAK = 0.002;
const LIVENESS_SCORE_MIN = 3;

const Ic = {
  User: () => <svg viewBox="0 0 24 24"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></svg>,
  Camera: () => <svg viewBox="0 0 24 24"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" /><circle cx="12" cy="13" r="4" /></svg>,
  Check: () => <svg viewBox="0 0 24 24"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" /></svg>,
  Alert: () => <svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></svg>,
  Warn: () => <svg viewBox="0 0 24 24"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" /></svg>,
  Refresh: () => <svg viewBox="0 0 24 24"><polyline points="1 4 1 10 7 10" /><path d="M3.51 15a9 9 0 1 0 .49-3.89" /></svg>,
  X: () => <svg viewBox="0 0 24 24"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>,
  Arrow: () => <svg viewBox="0 0 24 24"><line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" /></svg>,
  Lock: () => <svg viewBox="0 0 24 24"><rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></svg>,
  Users: () => <svg viewBox="0 0 24 24"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></svg>,
  Move: () => <svg viewBox="0 0 24 24"><polyline points="5 9 2 12 5 15" /><polyline points="9 5 12 2 15 5" /><line x1="2" y1="12" x2="22" y2="12" /><line x1="12" y1="2" x2="12" y2="22" /><polyline points="19 9 22 12 19 15" /><polyline points="9 19 12 22 15 19" /></svg>,
  Face: () => <svg viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" /><path d="M8 14s1.5 2 4 2 4-2 4-2" /><line x1="9" y1="9" x2="9.01" y2="9" /><line x1="15" y1="9" x2="15.01" y2="9" /></svg>,
  Eye: () => <svg viewBox="0 0 24 24"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg>,
};

const SmallCheck = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
    <path d="M2.5 7l3 3L11.5 4" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" />
  </svg>
);

const FACE_STATUS = {
  loading: { cls: 'loading', icon: null, msg: 'Initialising face detection…' },
  idle: { cls: 'loading', icon: null, msg: 'Waiting for camera…' },
  'no-face': { cls: 'no-face', icon: 'Alert', msg: 'No face detected — look at the camera' },
  multiple: { cls: 'multiple', icon: 'Users', msg: 'Multiple faces detected — one person only' },
  'too-small': { cls: 'warn', icon: 'Move', msg: 'Move closer to the camera' },
  'off-center': { cls: 'warn', icon: 'Move', msg: 'Centre your face in the oval' },
  scanning: { cls: 'detecting', icon: null, msg: 'Analysing face…' },
  'photo-detected': { cls: 'no-face', icon: 'Alert', msg: 'Live face not detected — please use your real face' },
  detecting: { cls: 'detecting', icon: 'Eye', msg: 'Blink twice to continue…' },
  holding: { cls: 'detecting', icon: 'Eye', msg: 'Hold still…' },
  ready: { cls: 'ready', icon: 'Check', msg: 'Face verified — capture now' },
};


class BiometricSelfie extends Component {

  constructor(props) {
    super(props);
    this.state = {
      step: 1,
      modelsLoaded: false,
      modelError: false,
      cameraActive: false,
      stream: null,
      faceStatus: 'loading',
      livenessMs: 0,
      captureEnabled: false,
      blinkCount: 0,
      capturedImage: null,
      isCapturing: false,
      status: null,
      errorMsg: '',
      customerDetails: { name: '', dob: '', email: '', plan: '' },
      selfie_id: '',
      bio_prep_actid: '',
      loading: false,
      activationType: '',
      showErrorPage: false,
      apiErrorMessage: '',
      scanProgress: 0,
      livenessVerified: false,
    };

    this.videoRef = createRef();
    this.canvasRef = createRef();
    this._detectionTimer = null;
    this._faceReadySince = null;
    this._mounted = false;
    this._blinkCount = 0;
    this._isEyeClosed = false;
    this._lastBlinkTime = 0;
    // Passive scan
    this._earSamples = [];
    this._relXSamples = [];
    this._relYSamples = [];
    this._livenessVerified = false;
    this._livenessFailed = false;

    // IOD stability for blink
    this._iodHistory = [];
    this._iodWasStableAtClose = false;

  }

  async componentDidMount() {
    this._mounted = true;
    await this.loadModels();
    this.toggleChatVisibility();
    const params = new URLSearchParams(window.location.search);
    const selfie_id = params.get('selfie_id');
    const bio_prep_actid = params.get('bio_prep_actid');
    const bio_postpaid_actid = params.get('bio_postpaid_actid');
    const sdId= params.get("sd");
    const prepaidId= params.get("pr");
    const postpaidId= params.get("sm");


    console.log("sdId==>",sdId)
    console.log("prepaidId==>",postpaidId)


    if (selfie_id && bio_prep_actid) {
      this.fetchActivationDetails(selfie_id, bio_prep_actid, 'prepaid');
    } else if (sdId && prepaidId) {
      this.fetchActivationDetails(sdId, prepaidId, 'prepaid-pr');
    } else if (sdId && postpaidId) {
      this.fetchActivationDetails(sdId, postpaidId, 'postpaid-sm');
    } else if (selfie_id && bio_postpaid_actid) {
      this.fetchActivationDetails(selfie_id, bio_postpaid_actid, 'postpaid');
    }
  }

  componentWillUnmount() {
    this._mounted = false;
    this.stopDetection();
    this.stopStream();
  }

  safeSetState(state, callback) {
    if (this._mounted) this.setState(state, callback);
  }

  resetBlink() {
    // Blink state
    this._blinkCount = 0;
    this._isEyeClosed = false;
    this._lastBlinkTime = 0;
    this._faceReadySince = null;
    this._eyeClosedSince = null;
    this._lastFaceBox = null;
    this._movementAccum = 0;
    // Passive scan — reset so scan restarts when face reappears
    this._earSamples = [];
    this._relXSamples = [];
    this._relYSamples = [];
    this._livenessVerified = false;
    this._livenessFailed = false;
    // IOD
    this._iodHistory = [];
    this._iodWasStableAtClose = false;
  }

  loadModels = async () => {
    const modelsUrl = `${window.location.origin}${MODELS_URL}`;
    const manifestUrl = `${modelsUrl}/tiny_face_detector_model-weights_manifest.json`;
    console.log('[BiometricSelfie] Loading models from:', modelsUrl);
    try {
      const probe = await fetch(manifestUrl);
      if (!probe.ok) throw new Error(`Manifest HTTP ${probe.status}. URL: ${manifestUrl}`);
      await Promise.all([
        faceapi.nets.tinyFaceDetector.loadFromUri(modelsUrl),
        faceapi.nets.faceLandmark68TinyNet.loadFromUri(modelsUrl),
      ]);
      console.log('[BiometricSelfie] Models loaded successfully');
      this.safeSetState({ modelsLoaded: true });
    } catch (err) {
      console.error('[BiometricSelfie] Model load failed:', err.message || err);
      this.safeSetState({ modelsLoaded: false, modelError: true });
    }
  };

  goToStep2 = () => this.setState({ step: 2 }, () => this.startCamera());

  goBack = () => {
    if (this.state.step === 2) {
      this.stopDetection();
      this.stopStream();
      this.resetBlink();
      this.setState({
        step: 1, cameraActive: false, stream: null,
        capturedImage: null, status: null, errorMsg: '',
        faceStatus: 'idle', livenessMs: 0, captureEnabled: false, blinkCount: 0,
      });
    } else if (this.props.onBack) {
      this.props.onBack();
    }
  };


  startCamera = async () => {
    this.resetBlink();
    this.setState({
      capturedImage: null, status: null, errorMsg: '',
      faceStatus: 'idle', livenessMs: 0, captureEnabled: false, blinkCount: 0,
    });
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: { ideal: 640 }, height: { ideal: 800 } },
        audio: false,
      });
      this.setState({ cameraActive: true, stream }, () => {
        const video = this.videoRef.current;
        if (!video) return;
        video.srcObject = stream;
        video.play().catch(() => { });
        if (video.readyState >= 2) {
          this.startDetection();
        } else {
          video.addEventListener('loadeddata', () => this.startDetection(), { once: true });
        }
        setTimeout(() => { if (!this._detectionTimer) this.startDetection(); }, 1000);
      });
    } catch {
      this.safeSetState({
        cameraActive: false,
        status: 'error',
        errorMsg: 'Camera access was denied. Please allow camera permissions in your browser and try again.',
      });
    }
  };

  stopStream() {
    const { stream } = this.state;
    if (stream) stream.getTracks().forEach(t => t.stop());
  }

  startDetection = () => {
    if (this._detectionTimer) return;
    this._detectionTimer = setInterval(this.runDetection, DETECTION_INTERVAL_MS);
  };

  stopDetection = () => {
    if (this._detectionTimer) { clearInterval(this._detectionTimer); this._detectionTimer = null; }
  };

  calcEAR = (pts) => {
    const d = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);
    return (d(pts[1], pts[5]) + d(pts[2], pts[4])) / (2 * d(pts[0], pts[3]));
  };

  // Standard deviation helper
  _stdDev = (arr) => {
    if (arr.length < 2) return 0;
    const mean = arr.reduce((a, b) => a + b, 0) / arr.length;
    return Math.sqrt(arr.reduce((a, b) => a + (b - mean) ** 2, 0) / arr.length);
  };

  // Score 0–4: ≥3 = real face, <3 = photo
  _scoreLiveness = () => {
    const earSD = this._stdDev(this._earSamples);
    const relXSD = this._stdDev(this._relXSamples);
    const relYSD = this._stdDev(this._relYSamples);
    const landmarkSD = Math.max(relXSD, relYSD);

    let score = 0;
    if (earSD >= EAR_STDDEV_STRONG) score += 2;
    else if (earSD >= EAR_STDDEV_WEAK) score += 1;
    if (landmarkSD >= LANDMARK_STDDEV_STRONG) score += 2;
    else if (landmarkSD >= LANDMARK_STDDEV_WEAK) score += 1;

    console.log(`[Liveness] score=${score} earSD=${earSD.toFixed(4)} landmarkSD=${landmarkSD.toFixed(4)}`);
    return score;
  };

  // Inter-ocular distance (eye outer corners)
  calcIOD = (landmarks) => {
    const pts = landmarks.positions;
    return Math.hypot(pts[45].x - pts[36].x, pts[45].y - pts[36].y);
  };












  runDetection = async () => {
    const { modelsLoaded, cameraActive, capturedImage } = this.state;
    const video = this.videoRef.current;
    if (!modelsLoaded || !cameraActive || capturedImage || !video || video.videoWidth === 0) return;

    try {
      const options = new faceapi.TinyFaceDetectorOptions({ inputSize: 320, scoreThreshold: 0.35 });
      const detections = await faceapi.detectAllFaces(video, options).withFaceLandmarks(true);
      if (!this._mounted) return;

      /* ── 1. Face count ── */
      if (detections.length === 0) {
        this.resetBlink();
        return this.safeSetState({ faceStatus: 'no-face', livenessMs: 0, captureEnabled: false, blinkCount: 0, scanProgress: 0 });
      }
      if (detections.length > 1) {
        this.resetBlink();
        return this.safeSetState({ faceStatus: 'multiple', livenessMs: 0, captureEnabled: false, blinkCount: 0, scanProgress: 0 });
      }

      /* ── 2. Size & position ── */
      const det = detections[0];
      const box = det.detection ? det.detection.box : det.box;
      const vw = video.videoWidth || 320;
      const vh = video.videoHeight || 480;

      if (!box || box.height / vh < MIN_FACE_HEIGHT_RATIO) {
        this.resetBlink();
        return this.safeSetState({ faceStatus: 'too-small', livenessMs: 0, captureEnabled: false, blinkCount: 0, scanProgress: 0 });
      }
      const faceCx = (box.x + box.width / 2) / vw;
      const faceCy = (box.y + box.height / 2) / vh;
      if (Math.abs(faceCx - 0.5) > MAX_CENTER_OFFSET || Math.abs(faceCy - 0.48) > MAX_CENTER_OFFSET) {
        this.resetBlink();
        return this.safeSetState({ faceStatus: 'off-center', livenessMs: 0, captureEnabled: false, blinkCount: 0, scanProgress: 0 });
      }

      /* ── 3. Get landmarks (needed for both phases) ── */
      const leftEye = det.landmarks.getLeftEye();
      const rightEye = det.landmarks.getRightEye();
      if (!leftEye || !rightEye || leftEye.length < 6 || rightEye.length < 6) return;

      /* ── 4. PASSIVE LIVENESS SCAN ── */
      if (this._livenessFailed) {
        // Keep showing error until face disappears and comes back (resetBlink clears this)
        return this.safeSetState({ faceStatus: 'photo-detected', captureEnabled: false });
      }

      if (!this._livenessVerified) {
        // Signal A: EAR variance
        const avgEAR = (this.calcEAR(leftEye) + this.calcEAR(rightEye)) / 2;
        this._earSamples.push(avgEAR);

        // Signal B: normalised eye-centroid to nose-tip (landmark independence)
        const noseTip = det.landmarks.positions[30];
        const allEyePts = [...leftEye, ...rightEye];
        const eyeCx = allEyePts.reduce((s, p) => s + p.x, 0) / allEyePts.length;
        const eyeCy = allEyePts.reduce((s, p) => s + p.y, 0) / allEyePts.length;
        const fw = box.width || 1;
        this._relXSamples.push((eyeCx - noseTip.x) / fw);
        this._relYSamples.push((eyeCy - noseTip.y) / fw);

        const collected = this._earSamples.length;
        const scanProgress = Math.min(Math.round((collected / PASSIVE_SCAN_FRAMES) * 100), 99);
        this.safeSetState({ faceStatus: 'scanning', scanProgress, captureEnabled: false });

        if (collected >= PASSIVE_SCAN_FRAMES) {
          const score = this._scoreLiveness();
          if (score >= LIVENESS_SCORE_MIN) {
            this._livenessVerified = true;
            this.safeSetState({ livenessVerified: true, scanProgress: 100 });
          } else {
            this._livenessFailed = true;
            this.safeSetState({ faceStatus: 'photo-detected', scanProgress: 0, captureEnabled: false });
          }
        }
        return;
      }

      /* ── 5. IOD stability tracking ── */
      const iod = this.calcIOD(det.landmarks);
      this._iodHistory.push(iod);
      if (this._iodHistory.length > 12) this._iodHistory.shift();
      const avgIOD = this._iodHistory.reduce((a, b) => a + b, 0) / this._iodHistory.length;
      const iodStableNow = this._iodHistory.length >= 5 &&
        Math.abs(iod - avgIOD) / avgIOD < 0.09;

      /* ── 6. Blink detection via EAR + IOD guard ── */
      const avgEAR = (this.calcEAR(leftEye) + this.calcEAR(rightEye)) / 2;
      const now = Date.now();

      if (this._lastFaceBox) {
        this._movementAccum +=
          Math.abs(box.x - this._lastFaceBox.x) +
          Math.abs(box.y - this._lastFaceBox.y);
      }
      this._lastFaceBox = { x: box.x, y: box.y };

      if (avgEAR < EYE_AR_CLOSE && !this._isEyeClosed) {
        this._isEyeClosed = true;
        this._eyeClosedSince = now;
        this._iodWasStableAtClose = iodStableNow; // snapshot geometry at closure moment
      } else if (avgEAR > EYE_AR_OPEN && this._isEyeClosed) {
        const dur = now - (this._eyeClosedSince || now);
        this._isEyeClosed = false;
        this._eyeClosedSince = null;

        const realDuration = dur >= 80 && dur <= 400;
        const hasMovement = this._movementAccum > 1.5;
        const iodStable = this._iodWasStableAtClose; // face geometry didn't shift = not a photo tilt

        if (realDuration && hasMovement && iodStable && now - this._lastBlinkTime > 400) {
          this._lastBlinkTime = now;
          this._blinkCount++;
          console.log(`✅ Blink #${this._blinkCount} dur=${dur}ms iodStable=${iodStable}`);
          if (this._blinkCount >= REQUIRED_BLINKS && !this._faceReadySince) {
            this._faceReadySince = Date.now();
          }
          this.safeSetState({ blinkCount: this._blinkCount });
        } else {
          console.warn(`❌ Rejected blink dur=${dur}ms move=${hasMovement} iod=${iodStable}`);
        }
      }

      /* ── 7. Liveness hold & capture unlock ── */
      const elapsed = this._faceReadySince ? Date.now() - this._faceReadySince : 0;
      const livenessMs = Math.min(elapsed, LIVENESS_HOLD_MS);

      if (this._blinkCount >= REQUIRED_BLINKS && elapsed >= LIVENESS_HOLD_MS) {
        this.safeSetState({ faceStatus: 'ready', livenessMs: LIVENESS_HOLD_MS, captureEnabled: true });
      } else if (this._blinkCount >= REQUIRED_BLINKS) {
        this.safeSetState({ faceStatus: 'holding', livenessMs, captureEnabled: false });
      } else {
        this.safeSetState({ faceStatus: 'detecting', livenessMs: 0, captureEnabled: false });
      }

    } catch (err) {
      console.error('Detection frame error:', err);
    }
  };

  handleCapture = async () => {
    if (!this.state.captureEnabled) return;
    const video = this.videoRef.current;
    const canvas = this.canvasRef.current;
    if (!video || !canvas) return;

    this.setState({ isCapturing: true });
    canvas.width = video.videoWidth || 480;
    canvas.height = video.videoHeight || 600;
    const ctx = canvas.getContext('2d');
    ctx.save(); ctx.scale(-1, 1); ctx.drawImage(video, -canvas.width, 0, canvas.width, canvas.height); ctx.restore();

    try {
      const options = new faceapi.TinyFaceDetectorOptions({ inputSize: 224, scoreThreshold: 0.45 });
      const result = await faceapi.detectAllFaces(canvas, options).withFaceLandmarks(true);
      if (result.length === 0) {
        return this.safeSetState({ isCapturing: false, status: 'error', errorMsg: 'No face found in the captured image. Please retake your selfie.' });
      }
      if (result.length > 1) {
        return this.safeSetState({ isCapturing: false, status: 'error', errorMsg: 'Multiple faces detected. Please ensure only you are in the frame.' });
      }
    } catch { }

    const dataUrl = canvas.toDataURL('image/jpeg', 0.92);
    this.stopDetection();
    this.stopStream();
    this.safeSetState({
      capturedImage: dataUrl, cameraActive: false, stream: null,
      isCapturing: false, captureEnabled: false,
      faceStatus: 'idle', livenessMs: 0, status: 'success', errorMsg: '',
    });
  };

  handleRetake = () => {
    this.resetBlink();
    this.setState(
      {
        capturedImage: null, status: null, errorMsg: '', cameraActive: false, stream: null,
        faceStatus: 'idle', livenessMs: 0, captureEnabled: false, blinkCount: 0
      },
      () => this.startCamera()
    );
  };


  handleSubmit = async () => {
    try {
      const { capturedImage } = this.state;
      if (!capturedImage) {
        this.setState({ status: 'error', errorMsg: 'Please capture a selfie before submitting.' });
        return;
      }
      if (this.props.onSubmit) this.props.onSubmit({ selfieDataUrl: capturedImage });


      let payload = {};
      let url = '';
      if (this.state.activationType === 'prepaid-pr') {
        payload = { sd: this.state.selfie_id, pr: this.state.bio_prep_actid, selfie_data: capturedImage };
        url = 'activation/biometric-txn-url';

      } else if (this.state.activationType === 'postpaid-sm') {
        payload = { sd: this.state.selfie_id, sm: this.state.bio_prep_actid, selfie_data: capturedImage };
        url = 'postpaid/activation/biometric-txn-url-postpaid';

      } else {
        const key = this.state.activationType === 'prepaid' ? 'bio_prep_actid' : 'bio_postpaid_actid';
        payload = { selfie_id: this.state.selfie_id, [key]: this.state.bio_prep_actid, selfie_data: capturedImage };
        url = this.state.activationType === 'prepaid' ? 'activation/biometric-txn-url' : 'postpaid/activation/biometric-txn-url-postpaid';
      }



      // const key = this.state.activationType === 'prepaid' ? 'bio_prep_actid' : 'bio_postpaid_actid';
      // const payload = { selfie_id: this.state.selfie_id, [key]: this.state.bio_prep_actid, selfie_data: capturedImage };
      // const url = this.state.activationType === 'prepaid' ? 'activation/biometric-txn-url' : 'postpaid/activation/biometric-txn-url-postpaid';
      this.setState({ loading: true });
      const headers = await getApiHeaderGuest();
      const response = await axios.post(`${SERVER_URL}${url}`, payload, { headers });

      if (response.status === 200) {
        this.setState({ loading: false });
        if (response.data?.data) window.location.replace(response.data.data);
      }
    } catch (error) {
      console.error(error);
      this.setState({
        loading: false,
        showErrorPage: true,
        apiErrorMessage: error?.response?.data?.message || 'Something went wrong while processing biometric verification.',
      });
    }
  };


  toggleChatVisibility() {
    const t = setInterval(() => {
      const el = document.getElementById('zsiq_float');
      if (el) { el.style.display = 'none'; clearInterval(t); }
    }, 100);
  }

  fetchActivationDetails = async (selfieId, activationId, type) => {
    this.setState({ activationType: type });

  
    let key = "";
    let payload = {};
    let url = '';

    if (type === "prepaid-pr") {
      payload = {
        sd: selfieId,
        pr: activationId,
      };
        url = 'activation/biometric-selfie';

    } else if (type === "postpaid-sm") {
      payload = {
        sd: selfieId,
        sm: activationId,
      };

       url = 'postpaid/activation/biometric-selfie-postpaid';

    } else {
      key = type === "prepaid"? "bio_prep_actid": "bio_postpaid_actid";
      payload = {
        selfie_id: selfieId,
        [key]: activationId,
      };
       url = this.state.activationType ===  'prepaid' ? 'activation/biometric-selfie' : 'postpaid/activation/biometric-selfie-postpaid';
    }
    try {
      this.setState({ selfie_id: selfieId, bio_prep_actid: activationId, loading: true });
      const headers = await getApiHeaderGuest();
      // const url = type === 'prepaid' ? 'activation/biometric-selfie' : 'postpaid/activation/biometric-selfie-postpaid';
      const response = await axios.post(`${SERVER_URL}${url}`, payload, { headers });
      if (response.status === 200) {
        const d = response.data?.data;
        this.setState({
          loading: false,
          customerDetails: { name: d?.name, email: d?.email, dob: d?.dob, plan: d?.plan },
        });
      }
    } catch (error) {
      console.error(error);
      this.setState({
        loading: false,
        showErrorPage: true,
        apiErrorMessage: error?.response?.data?.message || 'Something went wrong while fetching your activation details.',
      });
    }
  };


  renderHeader() {
    return (
      <header className="biometric-selfie-header">
        <div className="biometric-selfie-logo">
          <div className="biometric-selfie-logo-mark">
            <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path d="M2 20h3V10H2v10zm5 0h3V4h-3v16zm5 0h3v-8h-3v8zm5 0h3v-5h-3v5z" />
            </svg>
          </div>
          <span className="biometric-selfie-logo-name">Telsim</span>
        </div>
        <div className="biometric-selfie-header-tag">
          <span className="biometric-selfie-header-dot" />Secure
        </div>
      </header>
    );
  }

  renderStepper() {
    const { step } = this.state;
    const steps = ['Review Details', 'Take Selfie'];
    return (
      <div className="biometric-selfie-stepper">
        {steps.map((label, i) => {
          const n = i + 1, done = step > n, active = step === n;
          return (
            <React.Fragment key={i}>
              <div className="biometric-selfie-stepper-node">
                <div className={`biometric-selfie-stepper-circle${done ? ' done' : active ? ' active' : ''}`}>
                  {done ? <SmallCheck /> : n}
                </div>
                <span className={`biometric-selfie-stepper-label${done ? ' done' : active ? ' active' : ''}`}>{label}</span>
              </div>
              {i < steps.length - 1 && <div className={`biometric-selfie-stepper-line${done ? ' done' : ''}`} />}
            </React.Fragment>
          );
        })}
      </div>
    );
  }

  renderStep1() {
    const { name, dob, email } = this.state.customerDetails;
    const fields = [
      { label: 'Full name', value: name, pink: true },
      { label: 'Date of birth', value: dob },
      { label: 'Email address', value: email, span: true },
    ];
    return (
      <>
        <div className="biometric-selfie-section-head">
          <h1 className="biometric-selfie-section-title">Biometric Verification</h1>
          <p className="biometric-selfie-section-sub">Review your details below, then proceed to take your live selfie.</p>
        </div>
        <div className="biometric-selfie-card">
          <div className="biometric-selfie-card-head">
            <div className="biometric-selfie-card-icon"><Ic.User /></div>
            <div>
              <div className="biometric-selfie-card-title">Your Details</div>
              <div className="biometric-selfie-card-sub">Submitted in the previous step</div>
            </div>
          </div>
          <div className="biometric-selfie-card-body">
            <div className="biometric-selfie-detail-grid">
              {fields.map((f, i) => (
                <div key={i} className={`biometric-selfie-detail-item${f.span ? ' span2' : ''}`}>
                  <div className="biometric-selfie-detail-label">{f.label}</div>
                  <div className={`biometric-selfie-detail-value${f.pink ? ' pink' : ''}`}>{f.value || '—'}</div>
                </div>
              ))}
            </div>
          </div>
          <div className="biometric-selfie-continue-row">
            <button className="biometric-selfie-btn biometric-selfie-btn-primary" onClick={this.goToStep2}>
              Continue to selfie &nbsp;<Ic.Arrow />
            </button>
          </div>
        </div>
        <div className="biometric-selfie-footer-note"><Ic.Lock /> All data is encrypted under Australian Privacy Act</div>
      </>
    );
  }

  renderStep2() {
    const {
      modelsLoaded, modelError, cameraActive, capturedImage,
      faceStatus, livenessMs, captureEnabled, isCapturing,
      status, errorMsg, blinkCount, scanProgress
    } = this.state;

    const VW = 260, VH = 320, cx = VW / 2, cy = VH / 2 - 10, rx = 86, ry = 108;
    const ovalColor = ({ ready: '#16a34a', detecting: '#2563eb', 'no-face': '#c11d6c', multiple: '#dc2626', 'too-small': '#d97706', 'off-center': '#d97706' })[faceStatus] || '#c11d6c';
    const livenessPercent = Math.round((livenessMs / LIVENESS_HOLD_MS) * 100);
    const fsCfg = FACE_STATUS[faceStatus] || FACE_STATUS.idle;
    const FsIcon = fsCfg.icon ? Ic[fsCfg.icon] : null;

    return (
      <>
        {/* <div className="biometric-selfie-section-head">
          <h1 className="biometric-selfie-section-title">Take your selfie</h1>
          <p className="biometric-selfie-section-sub">Centre your face in the oval and blink twice to unlock capture.</p>
        </div> */}
        <div className="biometric-selfie-card">
          <div className="biometric-selfie-card-head">
            <div className="biometric-selfie-card-icon"><Ic.Camera /></div>
            <div>
              <div className="biometric-selfie-card-title">Live Camera</div>
              <div className="biometric-selfie-card-sub">Live face and blink required</div>
            </div>
          </div>
          <div className="biometric-selfie-card-body">
            <div className="biometric-selfie-camera-zone">

              {!modelsLoaded && !modelError && !cameraActive && (
                <div className="biometric-selfie-model-loading">
                  <div className="biometric-selfie-spinner" /> Loading face detection — this takes a few seconds…
                </div>
              )}
              {modelError && (
                <div className="biometric-selfie-status biometric-selfie-status-error" style={{ width: '100%' }}>
                  <Ic.Alert /><div><strong>Face detection unavailable</strong>Could not load models. Check your connection and reload the page.</div>
                </div>
              )}

              {/* ── Video frame ── */}
              <div className="biometric-selfie-video-wrap" style={{ width: VW, height: VH }}>
                {cameraActive && !capturedImage && (
                  <>
                    <video ref={this.videoRef} className="biometric-selfie-video" autoPlay playsInline muted />
                    <svg className="biometric-selfie-oval-svg" viewBox={`0 0 ${VW} ${VH}`} xmlns="http://www.w3.org/2000/svg">
                      <defs>
                        <mask id="bs-mask">
                          <rect width={VW} height={VH} fill="white" />
                          <ellipse cx={cx} cy={cy} rx={rx} ry={ry} fill="black" />
                        </mask>
                      </defs>
                      <rect width={VW} height={VH} fill="rgba(0,0,0,0.52)" mask="url(#bs-mask)" />
                      <ellipse cx={cx} cy={cy} rx={rx} ry={ry} fill="none" stroke={ovalColor} strokeWidth="2.5" style={{ transition: 'stroke 0.3s' }} />
                      {(faceStatus === 'detecting' || faceStatus === 'ready') && (
                        <ellipse cx={cx} cy={cy} rx={rx} ry={ry} fill="none" stroke={ovalColor} strokeWidth="1.5" strokeDasharray="30 400" className="biometric-selfie-ring-spin" opacity="0.6" />
                      )}
                      <text x={cx} y={cy + ry + 17} textAnchor="middle" fill="rgba(255,255,255,0.55)" fontSize="9" fontFamily="-apple-system,sans-serif" fontWeight="600" letterSpacing="1.4">ALIGN FACE</text>
                    </svg>
                  </>
                )}
                {capturedImage && <img src={capturedImage} alt="Captured selfie" className="biometric-selfie-preview-img" />}
                {!cameraActive && !capturedImage && !modelError && (
                  <div className="biometric-selfie-idle">
                    <div className="biometric-selfie-idle-icon"><Ic.Camera /></div>
                    <span className="biometric-selfie-idle-text">Tap "Open Camera" to begin</span>
                  </div>
                )}
                <canvas ref={this.canvasRef} className="biometric-selfie-canvas" />
              </div>

              {/* ── Face detection status bar ── */}
              {cameraActive && !capturedImage && (
                <>
                  <div className={`biometric-selfie-face-bar ${fsCfg.cls}`}>
                    {FsIcon
                      ? <FsIcon />
                      : <div className="biometric-selfie-spinner" style={{ borderTopColor: 'currentColor' }} />
                    }
                    <span>{fsCfg.msg}</span>
                    {faceStatus === 'detecting' && (
                      <span className="biometric-selfie-blink-count">{blinkCount}/{REQUIRED_BLINKS}</span>
                    )}
                  </div>
                  {(faceStatus === 'detecting' || faceStatus === 'ready') && (
                    <div className="biometric-selfie-liveness-bar">
                      <div className="biometric-selfie-liveness-fill" style={{ width: `${livenessPercent}%` }} />
                    </div>
                  )}



                </>
              )}

              {/* ── Tips ── */}
              {!capturedImage && (
                <div className="biometric-selfie-tips">
                  {[{ icon: '☀️', text: 'Good lighting' }, { icon: '👁', text: 'Look straight' }, { icon: '📱', text: 'Eye-level' }].map((t, i) => (
                    <div key={i} className="biometric-selfie-tip">
                      <div className="biometric-selfie-tip-icon">{t.icon}</div>
                      <div className="biometric-selfie-tip-text">{t.text}</div>
                    </div>
                  ))}
                </div>
              )}

              {/* ── Buttons ── */}
              <div className="biometric-selfie-actions">
                {!cameraActive && !capturedImage && !modelError && (
                  <button className="biometric-selfie-btn biometric-selfie-btn-primary biometric-selfie-btn-full"
                    onClick={this.startCamera} disabled={!modelsLoaded}>
                    <Ic.Camera /> {modelsLoaded ? 'Open Camera' : 'Loading…'}
                  </button>
                )}
                {cameraActive && !capturedImage && (
                  <>
                    <button className="biometric-selfie-btn biometric-selfie-btn-primary"
                      onClick={this.handleCapture} disabled={!captureEnabled || isCapturing}
                      title={!captureEnabled ? 'Blink twice to unlock capture' : ''}>
                      <Ic.Camera /> {isCapturing ? 'Verifying…' : 'Capture'}
                    </button>
                    <button className="biometric-selfie-btn biometric-selfie-btn-neutral" onClick={this.goBack}>
                      <Ic.X /> Cancel
                    </button>
                  </>
                )}
                {capturedImage && (
                  <>
                    <button className="biometric-selfie-btn biometric-selfie-btn-outline" onClick={this.handleRetake}><Ic.Refresh /> Retake</button>
                    <button className="biometric-selfie-btn biometric-selfie-btn-primary" onClick={this.handleSubmit}>Submit <Ic.Arrow /></button>
                  </>
                )}
              </div>

              {status === 'success' && (
                <div className="biometric-selfie-status biometric-selfie-status-success">
                  <Ic.Check /><div><strong>Face confirmed</strong>Live face detected and captured. Tap Submit to continue.</div>
                </div>
              )}
              {status === 'error' && (
                <div className="biometric-selfie-status biometric-selfie-status-error">
                  <Ic.Alert /><div><strong>Verification failed</strong>{errorMsg}</div>
                </div>
              )}

            </div>
          </div>
        </div>
        <div className="biometric-selfie-footer-note">
          <button style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', fontSize: 12, display: 'flex', alignItems: 'center', gap: 4, fontFamily: 'inherit' }} onClick={this.goBack}>
            ← Back to details
          </button>
        </div>
      </>
    );
  }

  renderErrorPage() {
    const { apiErrorMessage } = this.state;
    return (
      <div className="biometric-selfie-error-page">
        <div className="biometric-selfie-error-card">
          <div className="biometric-selfie-error-header">
            <div className="biometric-selfie-error-icon"><Ic.Alert /></div>
            <div>
              <div className="biometric-selfie-error-title">Verification Failed</div>
              <div className="biometric-selfie-error-subtitle">We could not continue your biometric verification</div>
            </div>
          </div>
          <div className="biometric-selfie-error-body">
            <div className="biometric-selfie-error-message-box">
              <div className="biometric-selfie-error-message-icon"><Ic.Alert /></div>
              <div className="biometric-selfie-error-message-content">
                <strong>Error</strong>
                <div>{apiErrorMessage}</div>
              </div>
            </div>
            <div className="biometric-selfie-error-button-wrap">
              <button className="biometric-selfie-btn biometric-selfie-btn-primary biometric-selfie-error-btn" onClick={() => window.location.reload()}>
                <Ic.Refresh /> Try Again
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  render() {
    const { step, loading, showErrorPage } = this.state;
    return (
      <div className="biometric-selfie-page">
        {this.renderHeader()}
        <main className="biometric-selfie-main">
          {this.renderStepper()}
          {showErrorPage ? this.renderErrorPage() : (
            <>
              {loading && <PageLoader />}
              {step === 1 && this.renderStep1()}
              {step === 2 && this.renderStep2()}
            </>
          )}
        </main>
      </div>
    );
  }
}

export default BiometricSelfie;